/**
 * Fulfillment Types
 * TypeScript types mirroring the fulfillments database table
 * 
 * Real DB columns (verified from database):
 * - fulfillment_id (uuid, PK)
 * - ref_user_id (bigint)
 * - reward_id (uuid, FK to reward_catalog)
 * - transaction_id (uuid, FK to transactions)
 * - status (varchar) - PENDING, COMPLETED, FAILED
 * - initiated_at, completed_at, failed_at (timestamps)
 * - failure_reason (text)
 * - external_tracking_id (varchar)
 * - context_json (jsonb)
 * - created_at, updated_at (timestamps)
 * 
 * Related: reward_catalog.category for display colors
 * Categories: cgm, glp-1, reward cards
 */

// Fulfillment status types - from real DB
export type FulfillmentStatus = 'PENDING' | 'COMPLETED' | 'FAILED';

// Category display configuration - scalable approach
// Colors map to reward_catalog.category, NOT individual rewards
// Categories from actual reward_catalog table
export const CATEGORY_DISPLAY: Record<string, { color: string; label: string }> = {
  'cgm': { color: 'info', label: 'CGM' },
  'glp-1': { color: 'success', label: 'GLP-1' },
  'reward cards': { color: 'primary', label: 'Reward Cards' },
};

// Default for unknown categories
export const DEFAULT_CATEGORY_DISPLAY = { color: 'secondary', label: 'Unknown' };

// Get category display config with fallback
export function getCategoryDisplay(category: string): { color: string; label: string } {
  return CATEGORY_DISPLAY[category.toLowerCase()] || { 
    ...DEFAULT_CATEGORY_DISPLAY, 
    label: category 
  };
}

// Status display configuration
export const STATUS_DISPLAY: Record<FulfillmentStatus, { color: string; label: string }> = {
  PENDING: { color: 'warning', label: 'Pending' },
  COMPLETED: { color: 'success', label: 'Completed' },
  FAILED: { color: 'danger', label: 'Failed' },
};

// Main fulfillment response interface - matches real DB schema
export interface FulfillmentResponse {
  fulfillment_id: string;
  user_id: string;              // ref_user_id as string for display
  reward_id: string;
  transaction_id: string;
  status: FulfillmentStatus;
  initiated_at: string;
  completed_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  external_tracking_id: string | null;
  context_json: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  // Denormalized fields from joins
  reward_name?: string;
  reward_category?: string;    // From reward_catalog.category
  // Actionability indicator - false for migration/demo failures
  is_actionable: boolean;
}

// Status counts returned by the status-counts endpoint
export interface FulfillmentStatusCounts {
  PENDING: number;
  COMPLETED: number;
  FAILED: number;              // Total failed count
  FAILED_ACTIONABLE: number;   // Actionable failures (excludes migration/demo)
}

// Query parameters for filtering fulfillments
export interface FulfillmentQueryParams {
  user_id?: string;
  reward_id?: string;
  status?: FulfillmentStatus;
  category?: string;          // Filter by reward category
  from_date?: string;         // Filter by initiated_at range
  to_date?: string;
  limit?: number;
  offset?: number;
}

// Admin action types (UI-level concept for button labels)
export type FulfillmentAction = 'RETRY' | 'COMPLETE' | 'CANCEL';

// Update request for PATCH endpoint
export interface FulfillmentUpdateRequest {
  status: FulfillmentStatus;
  reason?: string;
}

// Map UI actions to target status for PATCH request
export function getTargetStatus(action: FulfillmentAction): FulfillmentStatus {
  switch (action) {
    case 'RETRY':
      return 'PENDING';
    case 'COMPLETE':
      return 'COMPLETED';
    case 'CANCEL':
      return 'FAILED';
  }
}

// Helper function to get status label
export function getStatusLabel(status: FulfillmentStatus): string {
  return STATUS_DISPLAY[status]?.label || status;
}

// Helper function to get status color
export function getStatusColor(status: FulfillmentStatus): string {
  return STATUS_DISPLAY[status]?.color || 'secondary';
}

// Check if action is available for status
export function isActionAvailable(status: FulfillmentStatus, action: FulfillmentAction): boolean {
  switch (action) {
    case 'RETRY':
      return status === 'FAILED';
    case 'COMPLETE':
      return status === 'PENDING' || status === 'FAILED';
    case 'CANCEL':
      return status === 'PENDING';
    default:
      return false;
  }
}

// Status options for dropdowns
export const FULFILLMENT_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
] as const;

// Category options for dropdowns - DEPRECATED: Use useRewardMetadata hook to fetch from backend
// Kept for backwards compatibility but should not be used in new code
export const CATEGORY_OPTIONS = [
  { value: 'cgm', label: 'CGM' },
  { value: 'glp-1', label: 'GLP-1' },
  { value: 'reward cards', label: 'Reward Cards' },
] as const;

